import {
  AfterViewInit,
  Component,
  computed,
  effect,
  EffectRef,
  ElementRef,
  inject,
  Injector,
  input,
  OnDestroy,
  output,
  Renderer2,
  runInInjectionContext,
  signal,
  viewChild,
} from '@angular/core';
import { LucideDynamicIcon, LucideX } from '@lucide/angular';
import { InlineButtonComponent } from '../../atoms/inline-button/inline-button.component';
import { TextDirective } from '../../typography/text.directive';
import { BodyScrollLockService } from '../../utils/body-lock-scroll/body-scroll-lock/body-scroll-lock.service';
import { MobileDetection } from '../../utils/mobile-detection/mobile-detection.util';
import { BottomSheetGestureDirective } from './directives/bottom-sheet-gesture/bottom-sheet-gesture.directive';
import { DialogHeaderOptions, DialogVariant } from './models/dialog';
import { animateDialogClose, resetDialogStyles } from './utils/dialog-animation.util';
import { FocusTrapService } from './utils/focus-trap/focus-trap.service';
import { createKeyboardInsetController, KeyboardInsetController } from './utils/keyboard-inset.util';
import { DialogOverlayContainer } from './utils/overlay-container/dialog-overlay-container';
import type { VirtualKeyboard } from './virtual-keyboard';

@Component({
  selector: 'joii-dialog',
  template: `
    <dialog
      #dialogElement
      (mousedown)="handleBackdropMouseDown($event)"
      (click)="handleBackdropClick($event)"
      (keydown)="handleKeyDown($event)"
      class="dialog"
      role="dialog"
      [attr.data-variant]="computedVariant()"
      [attr.data-flexible-sized]="
        variant() === 'flexible-sized-modal' || variant() === 'flexible-sized-responsive' ? true : null
      "
    >
      <div
        class="dialog-content"
        (click)="$event.stopPropagation()"
        #dialogContent
        [bottomSheetGesture]="computedVariant() === 'bottom-sheet'"
        [dialogElement]="dialogElement"
        (dismiss)="handleDismiss()"
      >
        @if (computedVariant() === 'bottom-sheet') {
          <div class="drag-handle" data-gesture-handle></div>
        }
        @if (headerOptions()) {
          <div class="dialog-header" [attr.data-title-align]="headerOptions()?.titleAlign ?? 'center'">
            <div class="dialog-header-start">
              @if (computedVariant() !== 'drawer' || headerOptions()?.prefixIcon) {
                @if (headerOptions()?.prefixIcon) {
                  <button
                    class="dialog-header-prefix-icon-button"
                    joii-inline-button
                    [variant]="'secondary'"
                    [borderless]="true"
                    (click)="handlePrefixIconClick($event)"
                  >
                    <svg
                      class="dialog-header-prefix-icon"
                      [lucideIcon]="headerOptions()?.prefixIcon ?? null"
                      [strokeWidth]="2"
                    />
                  </button>
                }
              }
              @if (headerOptions()?.titleAlign === 'left' && headerOptions()?.title) {
                <h2 joii-text [size]="8" weight="bold" class="dialog-header-title">{{ headerOptions()?.title }}</h2>
              }
            </div>
            <div class="dialog-header-center">
              @if (headerOptions()?.titleAlign !== 'left' && headerOptions()?.title) {
                <h2 joii-text [size]="8" weight="bold" class="dialog-header-title">{{ headerOptions()?.title }}</h2>
              }
            </div>

            <div class="dialog-header-suffix-icon-container">
              @if (headerOptions()?.suffixIcon) {
                <button
                  class="dialog-header-suffix-icon-button"
                  joii-inline-button
                  [variant]="'secondary'"
                  [borderless]="true"
                  (click)="handleSuffixIconClick($event)"
                >
                  <svg
                    class="dialog-header-suffix-icon"
                    [lucideIcon]="headerOptions()?.suffixIcon ?? null"
                    [strokeWidth]="2"
                  />
                </button>
              }
              @if (headerOptions()?.closeButton) {
                <button
                  class="dialog-close-button"
                  joii-inline-button
                  [variant]="'secondary'"
                  [borderless]="true"
                  (click)="handleCloseClick($event)"
                >
                  <svg class="dialog-close-icon" [lucideIcon]="closeIcon" [strokeWidth]="2" />
                </button>
              }
            </div>
          </div>
        }
        <div class="dialog-body">
          <ng-content></ng-content>
          <ng-content select="[dialog-footer]"></ng-content>
        </div>
      </div>
      <div #dialogOverlayContainer class="cdk-overlay-container"></div>
    </dialog>
  `,
  styleUrl: './dialog.component.css',
  imports: [BottomSheetGestureDirective, InlineButtonComponent, LucideDynamicIcon, TextDirective],
})
export class DialogComponent implements AfterViewInit, OnDestroy {
  private injector = inject(Injector);
  private renderer = inject(Renderer2);
  private focusTrapService = inject(FocusTrapService);
  private bodyScrollLockService = inject(BodyScrollLockService);
  private overlayContainer = inject(DialogOverlayContainer);

  readonly variant = input<DialogVariant>('responsive');
  readonly headerOptions = input<DialogHeaderOptions>();
  readonly isMobile = inject(MobileDetection).isMobile;

  readonly computedVariant = computed(() => {
    const v = this.variant();
    if ((v === 'responsive' || v === 'drawer-responsive' || v === 'flexible-sized-responsive') && this.isMobile()) {
      return 'bottom-sheet';
    }
    if (v === 'drawer-responsive') {
      return 'drawer';
    }
    return v;
  });

  private dialogElement = viewChild.required<ElementRef<HTMLDialogElement>>('dialogElement');
  private dialogContent = viewChild<ElementRef<HTMLDivElement>>('dialogContent');
  private dialogOverlayContainerRef = viewChild.required<ElementRef<HTMLDivElement>>('dialogOverlayContainer');

  private keyboardInset!: KeyboardInsetController;
  private applyInsetEffectCleanup: EffectRef | null = null;

  private isClosing = signal(false);
  private inset = signal(0);
  private pressStartedOnBackdrop = false;
  /** True when this instance acquired the shared body scroll lock. */
  private holdsBodyScrollLock = false;

  readonly closeIcon = LucideX;

  readonly closeClick = output<Event>();
  readonly suffixIconClick = output<Event>();
  readonly prefixIconClick = output<Event>();

  readonly closed = output<void>();

  constructor() {
    this.setupVirtualKeyboard();
  }

  ngAfterViewInit(): void {
    this.keyboardInset = createKeyboardInsetController();
    this.setupKeyboardInsetEffect();
  }

  ngOnDestroy(): void {
    if (this.applyInsetEffectCleanup) {
      this.applyInsetEffectCleanup.destroy();
    }
    if (this.keyboardInset) {
      this.keyboardInset.destroy();
    }

    // Route changes destroy the dialog without going through close(). Without this,
    // body stays `position: fixed` and the next page renders off-screen (blank until refresh).
    try {
      const dialog = this.dialogElement()?.nativeElement;
      if (dialog?.open) {
        try {
          dialog.close();
        } catch {
          /* already closing or detached */
        }
        this.overlayContainer.removeContainer();
      }
    } catch {
      /* view already torn down */
    }

    if (this.holdsBodyScrollLock) {
      this.bodyScrollLockService.unlock();
      this.holdsBodyScrollLock = false;
    }
    this.focusTrapService.destroy();
  }

  open(): void {
    const dialog = this.dialogElement().nativeElement;
    if (dialog.open) return;

    this.focusTrapService.storePreviousActiveElement();
    const alreadyLocked = this.bodyScrollLockService.locked;
    this.bodyScrollLockService.lock();
    this.holdsBodyScrollLock = !alreadyLocked;

    dialog.showModal();

    // Mirror document theme onto dialog so top-layer inherits CSS variables
    const theme = document.documentElement.getAttribute('data-theme');
    if (theme) {
      this.renderer.setAttribute(dialog, 'data-theme', theme);
    } else {
      this.renderer.removeAttribute(dialog, 'data-theme');
    }

    // Add overlay container (CDK overlays and ng-primitives select/dropdown use this)
    this.overlayContainer.addContainer(this.dialogOverlayContainerRef().nativeElement);

    // Delay focus trap setup to allow Angular to render dynamic content
    // Use requestAnimationFrame to ensure content is rendered after change detection
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.focusTrapService.setupFocusTrap(dialog);
        this.focusTrapService.focusFirst(dialog);
      });
    });
  }

  async close(options?: { immediate?: boolean }): Promise<void> {
    const dialog = this.dialogElement().nativeElement;
    if (!dialog.open || this.isClosing()) return;

    const immediate = options?.immediate === true;

    // Animate bottom sheet or drawer close
    const variant = this.computedVariant();
    if (!immediate && (variant === 'bottom-sheet' || variant === 'drawer')) {
      this.isClosing.set(true);

      const content = this.dialogContent()?.nativeElement;
      if (content) {
        await animateDialogClose({
          content,
          dialog,
          duration: 280,
          renderer: this.renderer,
          variant,
        });
      }
    }

    this.finishClose();
  }

  handleBackdropMouseDown(event: MouseEvent): void {
    this.pressStartedOnBackdrop = this.isBackdropTarget(event.target as Node | null);
  }

  handleBackdropClick(event: MouseEvent): void {
    // The press must both start and end on the backdrop. Otherwise content that
    // shrinks between mousedown and mouseup (e.g. a dismissing dropdown) leaves the
    // pointer over the backdrop and the browser reports the click on the dialog.
    const startedOnBackdrop = this.pressStartedOnBackdrop;
    this.pressStartedOnBackdrop = false;
    if (!startedOnBackdrop) return;

    if (this.isBackdropTarget(event.target as Node | null)) {
      this.close();
    }
  }

  handleKeyDown(event: KeyboardEvent): void {
    const dialog = this.dialogElement().nativeElement;
    if (!dialog.open) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'Tab') {
      this.focusTrapService.trapFocus(event);
    }
  }

  handleDismiss(): void {
    this.close();
  }

  handleSuffixIconClick(event: Event): void {
    this.suffixIconClick.emit(event);
  }

  handlePrefixIconClick(event: MouseEvent): void {
    this.prefixIconClick.emit(event);
  }

  handleCloseClick(event: Event): void {
    this.closeClick.emit(event);
    this.close();
  }

  private isBackdropTarget(target: Node | null): boolean {
    const content = this.dialogContent()?.nativeElement;
    if (!content || !target) return false;
    if (content.contains(target)) return false;
    const overlayContainer = this.dialogOverlayContainerRef()?.nativeElement;
    return !(overlayContainer?.contains(target) ?? false);
  }

  private setupVirtualKeyboard(): void {
    try {
      const nav = window.navigator as Navigator & { virtualKeyboard?: VirtualKeyboard };
      if (nav.virtualKeyboard) {
        nav.virtualKeyboard.overlaysContent = true;
      }
    } catch {
      // Virtual keyboard API not supported - this is fine
    }
  }

  private setupKeyboardInsetEffect(): void {
    runInInjectionContext(this.injector, () => {
      this.applyInsetEffectCleanup = effect(() => {
        const dialog = this.dialogElement().nativeElement;
        const content = this.dialogContent()?.nativeElement;
        if (!dialog) return;

        const inset = this.keyboardInset.insetPx();
        this.inset.set(inset);

        if (!dialog.open) {
          this.renderer.removeStyle(dialog, 'padding-bottom');
          if (content) {
            this.renderer.removeStyle(content, 'max-height');
          }
          return;
        }

        if (inset > 0) {
          this.renderer.setStyle(dialog, 'padding-bottom', `${inset}px`);
          if (content) {
            const variant = this.computedVariant();
            const maxHeight = variant === 'fullscreen' ? `calc(100dvh - ${inset}px)` : `calc(100vh - ${inset}px - 2em)`;
            this.renderer.setStyle(content, 'max-height', maxHeight);
          }
        } else {
          this.renderer.removeStyle(dialog, 'padding-bottom');
          if (content) {
            this.renderer.removeStyle(content, 'max-height');
          }
        }
      });
    });
  }

  private finishClose(): void {
    const dialog = this.dialogElement().nativeElement;

    try {
      dialog.close();
    } catch {
      // Dialog may already be closed - ignore
    }

    this.overlayContainer.removeContainer();
    const content = this.dialogContent()?.nativeElement;
    resetDialogStyles(dialog, content, this.renderer);

    if (this.holdsBodyScrollLock) {
      this.bodyScrollLockService.unlock();
      this.holdsBodyScrollLock = false;
    }
    this.focusTrapService.restorePreviousFocus();
    this.focusTrapService.destroy();

    this.isClosing.set(false);
    this.pressStartedOnBackdrop = false;
    this.closed.emit();
  }
}
